import {
  MAX_CHARACTER_EMAIL_VALIDATION,
  MAX_CHARACTER_INPUT_VALIDATION,
  MAX_CHARACTER_PHONE_VALIDATION,
  MAX_PARTNER_NAME_INPUT_VALIDATION,
  MAX_TEXT_INPUT_VALIDATION,
} from "@/constants/common";
import { validator } from "@/utils/validator";

const invalidDayOfMonth = (label: string) => `「${label}」は1〜31の範囲で入力してください。`;

const dayOfMonth = (label: string) =>
  validator
    .string()
    .required()
    .numbersOnly()
    .test("day-of-month", invalidDayOfMonth(label), (value) => {
      if (!value) return true;
      const day = Number(value);
      return Number.isInteger(day) && day >= 1 && day <= 31;
    })
    .label(label);

export type TBillingRecipientRow = {
  locationType: string;
  locationName: string;
  addressee: string;
  zipCode?: string | null;
  prefecture: string;
  city: string;
  addressDetail: string;
  contactPerson: string;
  email: string;
};

export type TCompanyForm = {
  status: string;
  companyName: string;
  companyKanaName: string;
  contactPerson: string;
  companySpecificIndustryId: string;
  email: string;
  phoneNumber: string;
  zipCode?: string | null;
  prefecture: string;
  city: string;
  addressDetail: string;
  billingRecipients: TBillingRecipientRow[];
  mainPersonInChargeId: string;
  registrationMethod: string;
  invoiceDay: string;
  paymentDueDay: string;
  invoiceDateMonthOffset: string;
  paymentDueMonthOffset: string;
  joinMonthHandling: string;
  leaveMonthHandling: string;
  temporaryReturnHandling: string;
  leaveOfAbsenceHandling: string;
  importantMemo: string;
  companyMemo: string;
  billingAddressee: string;
  billingLocation: string;
  billingGrouping: string;
  travelExpenseBilling: string;
};

const billingRecipientSchema = validator.object().shape({
  locationType: validator.string().required().label("事業所／営業所選択"),
  locationName: validator.string().required().trim().limit(MAX_CHARACTER_INPUT_VALIDATION).label("事業所／営業所名"),
  addressee: validator.string().trim().limit(MAX_CHARACTER_INPUT_VALIDATION).label("宛名"),
  zipCode: validator.string().emptyToNull().autoTransformToHalfWidth().zipCode().trim().label("郵便番号").notRequired(),
  prefecture: validator.string().label("都道府県"),
  city: validator.string().label("市区町村"),
  addressDetail: validator.string().trim().limit(MAX_CHARACTER_INPUT_VALIDATION).label("町名丁目番地号等"),
  contactPerson: validator.string().trim().limit(MAX_CHARACTER_INPUT_VALIDATION).label("連絡担当者"),
  email: validator.string().email().limit(MAX_CHARACTER_EMAIL_VALIDATION).label("メールアドレス"),
});

export const companySchema = validator.object().shape({
  status: validator.string().required().label("ステータス"),
  companyName: validator.string().required().trim().limit(MAX_PARTNER_NAME_INPUT_VALIDATION).label("取引先名"),
  companyKanaName: validator.string().trim().autoTransformToFullWidth().furiganaOnly().limit(MAX_CHARACTER_INPUT_VALIDATION).label("ふりがな"),
  contactPerson: validator.string().trim().limit(MAX_CHARACTER_INPUT_VALIDATION).label("連絡先担当者"),
  companySpecificIndustryId: validator.string().required().label("代表分野"),
  email: validator.string().email().limit(MAX_CHARACTER_EMAIL_VALIDATION).label("メールアドレス"),
  phoneNumber: validator.string().isTelNumber().limit(MAX_CHARACTER_PHONE_VALIDATION).label("電話番号"),
  zipCode: validator.string().emptyToNull().autoTransformToHalfWidth().zipCode().trim().label("郵便番号").notRequired(),
  prefecture: validator.string().label("都道府県"),
  city: validator.string().label("市区町村"),
  addressDetail: validator.string().trim().limit(MAX_CHARACTER_INPUT_VALIDATION).label("町名丁目番地号等"),

  billingRecipients: validator.array().of(billingRecipientSchema).label("請求先情報"),

  mainPersonInChargeId: validator.string().required().label("主担当者"),
  registrationMethod: validator.string().label("登録方法"),

  invoiceDay: dayOfMonth("請求日"),
  paymentDueDay: dayOfMonth("支払期日"),
  invoiceDateMonthOffset: validator.string().required().label("請求日の月オフセット"),
  paymentDueMonthOffset: validator.string().required().label("支払期日の月オフセット"),
  joinMonthHandling: validator.string().required().label("入社月の扱い"),
  leaveMonthHandling: validator.string().required().label("退職月の扱い"),
  temporaryReturnHandling: validator.string().required().label("一時帰国の扱い"),
  leaveOfAbsenceHandling: validator.string().required().label("休職の扱い"),

  importantMemo: validator.string().trim().limit(MAX_TEXT_INPUT_VALIDATION).label("重要メモ"),
  companyMemo: validator.string().trim().limit(MAX_TEXT_INPUT_VALIDATION).label("取引先メモ"),

  billingAddressee: validator.string().required().trim().limit(MAX_CHARACTER_INPUT_VALIDATION).label("請求情報（宛名）"),
  billingLocation: validator.string().trim().limit(MAX_CHARACTER_INPUT_VALIDATION).label("請求先所在地"),
  billingGrouping: validator.string().required().label("請求グルーピング"),
  travelExpenseBilling: validator.string().label("渡航費請求"),
});

export const emptyBillingRecipientRow = (): TBillingRecipientRow => ({
  locationType: "",
  locationName: "",
  addressee: "",
  zipCode: "",
  prefecture: "",
  city: "",
  addressDetail: "",
  contactPerson: "",
  email: "",
});

export const toCompanyFormValues = (): TCompanyForm => ({
  status: "",
  companyName: "",
  companyKanaName: "",
  contactPerson: "",
  companySpecificIndustryId: "",
  email: "",
  phoneNumber: "",
  zipCode: "",
  prefecture: "",
  city: "",
  addressDetail: "",
  billingRecipients: [],
  mainPersonInChargeId: "",
  registrationMethod: "manual",
  invoiceDay: "",
  paymentDueDay: "",
  invoiceDateMonthOffset: "",
  paymentDueMonthOffset: "",
  joinMonthHandling: "",
  leaveMonthHandling: "",
  temporaryReturnHandling: "",
  leaveOfAbsenceHandling: "",
  importantMemo: "",
  companyMemo: "",
  billingAddressee: "",
  billingLocation: "",
  billingGrouping: "combined",
  travelExpenseBilling: "unset",
});
